import _ from 'lodash';
import Vue from 'vue';
import {mapActions, mapState, mapGetters} from 'vuex';
import moment from 'moment';
import 'moment-timezone';
import EventHub from '../../lib/eventHub';
import {AxiosResponse} from 'axios';
import {AlertNotificationModuleConstant, BioProfileModuleConstant, BioServicesConstant, EmployeeProfileModuleConstant, EventConstant, RoleConfigs, UrlModuleConstant, DPTConfig} from '../../lib/constants';
import {AlertNotificationType} from '../../lib/enums';
import ManageProfile from './manage-profile.html';
import BioAdmin from 'types/ba/bioAdmin';
import LtFramework from '../../../types/lt/ltFramework';
import BioProfileService from '../../lib/services/bioProfileService';
import BioProfileMuleService from '../../lib/services/bioProfileMuleService';
import BioConfigService from '../../lib/services/bioConfigService';
import AssetsService from '../../lib/services/assetsService';
import ContentFragmentService from '../../lib/services/contentFragmentService';

const bioProfileService: BioAdmin.IBioProfileService = new BioProfileService();
const bioProfileMuleService: BioAdmin.IBioProfileService = new BioProfileMuleService();
const bioConfigService: BioAdmin.IBioConfigService = new BioConfigService();
const assetsService: BioAdmin.IAssetsService = new AssetsService();
const contentFragmentService: BioAdmin.IContentFragmentService = new ContentFragmentService();

const visibleApproveSuccessMessage = 'You have approved {{name}}\'s bio! This bio is now live.';
const hiddenApproveSuccessMessage = 'You have approved {{name}}\'s bio but it is still hidden.';
const rejectSuccessMessage = 'You have denied {{name}}\'s bio. A message has been sent to {{name}}.';

const fixProfileStatusNotificationTitle: string = 'Performer Bio Updated';
const fixProfileStatusNotificationMessage: string = 'Role has been successfully updated from Workday. This bio should be updated and resubmitted for approval.'

interface IComponentData {
	bioProfileServiceLoading: boolean;
	roles: string[];
	biosConfigResponse: BioAdmin.IBiosConfigResponse;
	roleFeatures: string[];
	window: Window;
	profileParam: number;
	showStatusNotification: boolean;
	view: number;
	views: object[];
	publishStatus: string;
	publishedProfile: boolean;
	publicBioUrl: string;
	bioProfilePaths: BioAdmin.IBioProfilePaths;
	publicDptUrl: string;
	certifications: BioAdmin.ICertification[];
	specialties: BioAdmin.ITag[];
	services: BioAdmin.IServiceTag[];
	languages: BioAdmin.ITag[];
	clubs: BioAdmin.IProfileClub[];
	quote: string;
	quoteCitation: string;
	aboutMe: string;
	socialLinks: BioAdmin.ISocialLink[];
	spotify: string;
	instagram: string;
	facebook: string;
	tiktok: string;
	twitter: string;
	youtube: string;
	feedback: string;
	jobTitles: BioAdmin.IJobTitle[];
	visible: boolean;
	promoted: boolean;
	dptFlag: boolean;
	biosImages: BioAdmin.IBioProfileImage;
	pendingTestimonials: number;
	viewScheduleButton: boolean;
	viewServicesButton: boolean;
	useSpringApi: boolean;
}

export default Vue.component('manageProfile', {
	template: ManageProfile,
	data(): IComponentData {
		return {
			bioProfileServiceLoading: true,
			roles: [],
			biosConfigResponse:  {
				rolesFeatures: [
					{
						roleId: 1,
						roleName : 'Personal Trainer',
						features: [
							'contact',
							'quote'
						]
					}],
				features:
				{
					quote: {
						maxLength: '50',
					},
					contact: {}
				}
			},
			roleFeatures: [],
			window: window,
			profileParam: 0,
			showStatusNotification: false,
			view: 2,
			views: [
				{ text: 'View as Member', value: 2},
				{ text: 'View as Prospect', value: 1},
			],
			publishStatus: '',
			publishedProfile: false,
			publicBioUrl: '',
			publicDptUrl: '',
			bioProfilePaths: {},
			certifications: [],
			specialties: [],
			services: [],
			languages: [],
			clubs: [],
			quote: '',
			quoteCitation: '',
			aboutMe: '',
			socialLinks: [],
			spotify: '',
			instagram: '',
			facebook: '',
			tiktok: '',
			twitter: '',
			youtube: '',
			feedback: '',
			jobTitles: [],
			visible: false,
			promoted: false,
			dptFlag: false,
			pendingTestimonials: 0,
			viewScheduleButton: false,
			viewServicesButton: false,
			biosImages: {},
			useSpringApi: false,
		};
	},
	computed: {
		...mapState(BioProfileModuleConstant.name, [
			'bioProfile',
		]),
		...mapState(EmployeeProfileModuleConstant.name, [
			'employeeProfile',
		]),
		...mapGetters(UrlModuleConstant.name, {
			getGlobalPageDriverUrl: UrlModuleConstant.getter.getGlobalPageDriverUrl,
			getUrlParameter: UrlModuleConstant.getter.getUrlParameter,
		}),
		...mapGetters(AlertNotificationModuleConstant.name,  {
			getAlertNotificationState: 'getState',
		}),
		...mapGetters(EmployeeProfileModuleConstant.name, {
			getEmployeeId: EmployeeProfileModuleConstant.getter.getEmployeeId,
		}),
		...mapGetters(BioProfileModuleConstant.name, {
			getClubs: BioProfileModuleConstant.getter.getClubs,
			getPublishStatus: BioProfileModuleConstant.getter.getPublishStatus,
			getPublicBioUrl: BioProfileModuleConstant.getter.getPublicBioUrl,
			getBioProfilePaths: BioProfileModuleConstant.getter.getBioProfilePaths,
			getPublishedProfile: BioProfileModuleConstant.getter.getPublishedProfile,
			getCertifications: BioProfileModuleConstant.getter.getCertifications,
			getSpecialties: BioProfileModuleConstant.getter.getSpecialties,
			getSocialUrl: BioProfileModuleConstant.getter.getSocialUrl,
			getServices: BioProfileModuleConstant.getter.getServices,
			getLanguages: BioProfileModuleConstant.getter.getLanguages,
			getQuote: BioProfileModuleConstant.getter.getQuote,
			getQuoteCitation: BioProfileModuleConstant.getter.getQuoteCitation,
			getAboutMe: BioProfileModuleConstant.getter.getAboutMe,
			getSocialLinks: BioProfileModuleConstant.getter.getSocialLinks,
			getVisibility: BioProfileModuleConstant.getter.getVisibility,
			getPromoted: BioProfileModuleConstant.getter.getPromoted,
			getDPTFlag: BioProfileModuleConstant.getter.getDPTFlag,
			getJobTitles: BioProfileModuleConstant.getter.getJobTitles,
			getBiosImages: BioProfileModuleConstant.getter.getBiosImages,
		}),
		showPhoneNumber(): boolean {
			// show only if feature is on, is only a club lead, and a phone number exists
			return this.hasFeature('phone') && this.roles.includes(BioServicesConstant.clubLeadString) && this.roles.length === 1 && this.bioProfile.phoneNumber && this.bioProfile.phoneNumber.length > 0;
		},
		showDPTFlag(): boolean {
			const role = RoleConfigs.rolesFeatures.find(item => item.roleId === 2);
			const roleName = (role && role.roleName) || DPTConfig.roleName;
			return !!(this.hasRole(roleName) && this.enableDPTFlag());
		},
		classesBtnStyle(): string {
			if (this.hasFeature('trainingCTAs')) {
				if (this.viewScheduleButton && this.viewServicesButton) {
					return 'btn-link';
				} else if (this.viewScheduleButton || this.viewServicesButton) {
					return 'btn-primary-outline';
				} else {
					return 'btn-primary';
				}
			} else {
				return 'btn-primary';
			}
		},
	},
	methods: {
		...mapActions(AlertNotificationModuleConstant.name, {
			synchronizeAlertNotificationStore: AlertNotificationModuleConstant.action.synchronize,
			openAlert: AlertNotificationModuleConstant.action.open,
		}),
		...mapActions(BioProfileModuleConstant.name, {
			getBioProfile: BioProfileModuleConstant.action.getProfile,
		}),
		...mapActions(EmployeeProfileModuleConstant.name, {
			synchronizeEmployeeProfileStore: EmployeeProfileModuleConstant.action.synchronize,
		}),
		...mapActions(UrlModuleConstant.name, {
			synchronizeUrlStore: UrlModuleConstant.action.synchronize,
		}),
		refreshProfile(): void {
			// @ts-ignore
			this.getBioProfile(this.profileParam).then(() => {
				this.showStatusNotification = true;
				if (this.roles.length <= 0) {
					this.setFeaturesByRole();
				}
				if (this.hasFeature('responsibilities')) {
					this.getAssets(BioServicesConstant.contentFragmentType, BioServicesConstant.contentFragmentResponsibilitiesTag);
				}
			});
		},
		goTo(page: string, profileParam: number): void {
			if (profileParam) {
				// @ts-ignore
				window.location.assign(this.getGlobalPageDriverUrl('biosAdmin', page) + '?profile=' + profileParam);
			} else {
				// @ts-ignore
				window.location.assign(this.getGlobalPageDriverUrl('biosAdmin', page));
			}
		},
		hasFeature(feature: string): boolean {
			return this.roleFeatures.includes(feature);
		},
		hasRole(role: string): boolean {
			return this.roles.includes(role);
		},
		setFeaturesByRole() {
			this.roles = [];
			this.roleFeatures = [];
			// @ts-ignore
			_.forEach(this.bioProfile.roles, (profileRole: BioAdmin.IRole) => {
				const validRole: any = _.find(this.biosConfigResponse.rolesFeatures, (rc) => {
					return profileRole.name === rc.roleName;
				});
				this.roles.push(validRole.roleName);
				_.forEach(validRole.features, (feature) => {
					if (!this.roleFeatures.includes(feature)) {
						this.roleFeatures.push(feature);
					}
				});
			});
		},
		showMessage(message: string, title: string, type: AlertNotificationType): Promise<void> {
			const notifications: LtFramework.IAlertNotification[] = [];
			// @ts-ignore
			_.forEach(this.getAlertNotificationState, (notification) => {
				if (!notification.defer) {
					notifications.push(notification);
				}
			});
			if (message && message.length > 0) {
				const alert: LtFramework.IAlertNotification = {
					id: String(new Date().getTime()),
					type: type,
					message: message,
					dismissible: true,
					icon: true,
					title: title,
				};
				// @ts-ignore
				return this.openAlert(alert);
			} else {
				return new Promise<void>((resolve) => {
					resolve();
				});
			}
		},
		profileAction(action: string): Promise<void> {
			const statusObj: any = {
				action: action,
				feedback: this.feedback,
			};
			// @ts-ignore
			const id: number = this.profileParam || this.getEmployeeId;

			this.showStatusNotification = false;
			if (this.useSpringApi) {
				return bioProfileService.updateProfileStatus(id, statusObj)
					.then((response: AxiosResponse<BioAdmin.IBioProfileResponse>) => {
						this.refreshProfile();
						if (action === 'approve') {
							let msg: string;
							// @ts-ignore
							if (this.bioProfile.visible) {
								// @ts-ignore
								msg = visibleApproveSuccessMessage.replace('{{name}}', this.bioProfile.firstName + ' ' + this.bioProfile.lastNameInitial);
							} else {
								// @ts-ignore
								msg = hiddenApproveSuccessMessage.replace('{{name}}', this.bioProfile.firstName + ' ' + this.bioProfile.lastNameInitial);
							}
							this.showMessage(msg, '', AlertNotificationType.Success);
						} else if (action === 'reject') {
							let msg: string;
							// @ts-ignore
							msg = rejectSuccessMessage.replace(/{{name}}/g, this.bioProfile.firstName + ' ' + this.bioProfile.lastNameInitial);
							this.showMessage(msg, '', AlertNotificationType.Success);
						}
						// @ts-ignore
						$('#denialReason').modal('hide');
					});
			} else {
				return bioProfileMuleService.updateProfileStatus(id, statusObj)
					.then((response: AxiosResponse<BioAdmin.IBioProfileResponse>) => {
						this.refreshProfile();
						if (action === 'approve') {
							let msg: string;
							// @ts-ignore
							if (this.bioProfile.visible) {
								// @ts-ignore
								msg = visibleApproveSuccessMessage.replace('{{name}}', this.bioProfile.firstName + ' ' + this.bioProfile.lastNameInitial);
							} else {
								// @ts-ignore
								msg = hiddenApproveSuccessMessage.replace('{{name}}', this.bioProfile.firstName + ' ' + this.bioProfile.lastNameInitial);
							}
							this.showMessage(msg, '', AlertNotificationType.Success);
						} else if (action === 'reject') {
							let msg: string;
							// @ts-ignore
							msg = rejectSuccessMessage.replace(/{{name}}/g, this.bioProfile.firstName + ' ' + this.bioProfile.lastNameInitial);
							this.showMessage(msg, '', AlertNotificationType.Success);
						}
						// @ts-ignore
						$('#denialReason').modal('hide');
					});
			}
		},
		fixProfileStatus(): Promise<void> {
			if (this.useSpringApi) {
				return bioProfileService.fixProfileStatus()
					.then((response: AxiosResponse<BioAdmin.IBioProfileResponse>) => {
						this.refreshProfile();
						this.showMessage(fixProfileStatusNotificationMessage, fixProfileStatusNotificationTitle, AlertNotificationType.Success);
					});
			} else {
				return bioProfileMuleService.fixProfileStatus()
					.then((response: AxiosResponse<BioAdmin.IBioProfileResponse>) => {
						this.refreshProfile();
						this.showMessage(fixProfileStatusNotificationMessage, fixProfileStatusNotificationTitle, AlertNotificationType.Success);
					});
			}
		},
		getAssets(type: string, tags: string) {
			return assetsService.getAssetsByTag(type, tags)
				.then((response: AxiosResponse<BioAdmin.IContentAsset[]>) => {
					this.renderAsset(response.data);
				});
		},
		getRoleConfig() {
			return bioConfigService.getRoleConfig()
				.then((response: AxiosResponse<BioAdmin.IBiosConfigResponse>) => {
					this.biosConfigResponse = response.data;
				}).catch((error) => {
					// use config constant on localhost
					this.biosConfigResponse = RoleConfigs;
				});
		},
		renderAsset(data: BioAdmin.IContentAsset[]) {
			// get lead job title from profile and rename it to match expected content fragment name in AEM
			const leadTitle = _.filter(this.jobTitles, (jt: BioAdmin.IJobTitle) => jt.marketingTitle.indexOf('Manager') > -1);
			if (leadTitle && leadTitle.length > 0) {
				const titleName: string = leadTitle[0].marketingTitle.toLowerCase().replace(/ /g, '-');
				const asset: BioAdmin.IContentAsset[] = _.filter(data, (ca: BioAdmin.IContentAsset) => {
					return ca.name === titleName;
				});
				// get path to specific fragment from the asset path
				const fragmentPath = asset[0].path.split('content-fragments/');
				// send the fragment path section to the service to return the content and apply it to DOM
				return contentFragmentService.getContentFragment(fragmentPath[1])
					.then((response: AxiosResponse<any>) => {
						$('#responsibilitiesContent').html(response.data);
					});
			}
		},
		togglePromoted(value: boolean) {
			// @ts-ignore
			const id: number = this.profileParam || this.getEmployeeId;

			this.showStatusNotification = false;
			if (this.useSpringApi) {
				return bioProfileService.setPromoted(id, value)
					.then((response: AxiosResponse<BioAdmin.IBioProfileResponse>) => {
						this.refreshProfile();
					});
			} else {
				return bioProfileMuleService.setPromoted(id, value)
					.then((response: AxiosResponse<BioAdmin.IBioProfileResponse>) => {
						this.refreshProfile();
					});
			}
		},
		toggleVisibility(value: boolean) {
			// @ts-ignore
			const id: number = this.profileParam || this.getEmployeeId;

			this.showStatusNotification = false;
			if (this.useSpringApi) {
				return bioProfileService.setVisibility(id, value)
					.then((response: AxiosResponse<BioAdmin.IBioProfileResponse>) => {
						this.refreshProfile();
					});
			} else {
				return bioProfileMuleService.setVisibility(id, value)
					.then((response: AxiosResponse<BioAdmin.IBioProfileResponse>) => {
						this.refreshProfile();
					});
			}
		},
		toggleDPTFlag(value: boolean) {
			const id: number = this.profileParam || this.getEmployeeId;
			this.showStatusNotification = false;
			if (this.useSpringApi) {
				return bioProfileService.setDPTFlag(id, value).then(this.refreshProfile);
			} else {
				return bioProfileMuleService.setDPTFlag(id, value).then(this.refreshProfile);
			}
		},
		enableDPTFlag() {
			if (this.employeeProfile.roles) {
				const rolesGroup: LtFramework.IRoleGroup | undefined = this.employeeProfile.roles
					.find((role: LtFramework.IRoleGroup) => role.name === 'bios');
				return rolesGroup && rolesGroup.roles.map(role => role.name).includes(DPTConfig.biosAdmin);
			}
		    return false;
		},
		disableDPTFlag() {
			const enabled = _.find(Object.entries(this.bioProfile.biosImages),
					entry => entry[0].includes(DPTConfig.imageName) && entry[1]);
			return !enabled;
		},
		checkTestimonials(employeeId: number) {
			if (this.useSpringApi) {
				return bioProfileService.getTestimonials(employeeId).then((response: AxiosResponse<BioAdmin.ITestimonial[]>) => {
					const pendingTestimonials = _.filter(response.data, (testimonial: BioAdmin.ITestimonial) => testimonial.status === 'pending');
					this.pendingTestimonials = pendingTestimonials.length;
				});
			} else {
				return bioProfileMuleService.getTestimonials(employeeId).then((response: AxiosResponse<BioAdmin.ITestimonial[]>) => {
					const pendingTestimonials = _.filter(response.data, (testimonial: BioAdmin.ITestimonial) => testimonial.status === 'pending');
					this.pendingTestimonials = pendingTestimonials.length;
				});
			}
		},
		localTime(date: string): string {
			return moment.tz(date, moment.tz.guess()).format(BioServicesConstant.localDateFormat);
		},
		notEmptyObject(value?: any) {
			return !_.isEmpty(value);
		},
		getDptUrlFromPublicBioUrl(publicBioUrl: string): string {
			const bioProfilePath = this.bioProfilePaths ? this.bioProfilePaths['bio'] : '';
			const dptProfilePath = this.bioProfilePaths ? this.bioProfilePaths['dynamic-personal-training'] : '';

			if (bioProfilePath && dptProfilePath) {
				return publicBioUrl.replace('/' + bioProfilePath, dptProfilePath);
			} else {
				return publicBioUrl.replace('/bio', '/bio/dynamic-personal-training');
			}
		}

	},
	watch: {
		bioProfile() {
			// @ts-ignore
			this.clubs = this.getClubs;
			// @ts-ignore
			this.publishStatus = this.getPublishStatus;
			// @ts-ignore
			this.publicBioUrl = this.getPublicBioUrl;
			// @ts-ignore
			this.bioProfilePaths = this.getBioProfilePaths;
			// @ts-ignore
			this.publicDptUrl = this.getDptUrlFromPublicBioUrl(this.publicBioUrl);
			// @ts-ignore
			this.publishedProfile = this.getPublishedProfile;
			// @ts-ignore
			this.certifications = this.getCertifications;
			// @ts-ignore
			this.specialties = this.getSpecialties;
			// @ts-ignore
			this.services = this.getServices;
			// @ts-ignore
			this.languages = this.getLanguages;
			// @ts-ignore
			this.quote = this.getQuote;
			// @ts-ignore
			this.quoteCitation = this.getQuoteCitation;
			// @ts-ignore
			this.aboutMe = this.getAboutMe;
			// @ts-ignore
			this.socialLinks = this.getSocialLinks;
			// @ts-ignore
			this.spotify = this.getSocialUrl('spotify');
			// @ts-ignore
			this.instagram = this.getSocialUrl('instagram');
			// @ts-ignore
			this.facebook = this.getSocialUrl('facebook');
			// @ts-ignore
			this.tiktok = this.getSocialUrl('tiktok');
			// @ts-ignore
			this.twitter = this.getSocialUrl('twitter');
			// @ts-ignore
			this.youtube = this.getSocialUrl('youtube');
			// @ts-ignore
			this.visible = this.getVisibility;
			// @ts-ignore
			this.promoted = this.getPromoted;
			// @ts-ignore
			this.dptFlag = this.getDPTFlag;
			// @ts-ignore
			this.jobTitles = this.getJobTitles;
			// @ts-ignore
			this.biosImages = this.getBiosImages;
		},
	},
	created(): void {
		EventHub.$on(EventConstant.BioProfileServiceLoading, (loading: boolean) => {
			this.bioProfileServiceLoading = loading;
		});
		// @ts-ignore
		Promise.all([this.synchronizeAlertNotificationStore(), this.synchronizeEmployeeProfileStore(), this.synchronizeUrlStore(), this.getRoleConfig()]).then(() => {
			// @ts-ignore
			this.profileParam = this.getUrlParameter('profile');
			this.refreshProfile();
			if (this.profileParam > 0) {
				this.checkTestimonials(this.profileParam);
			}
			const featureFlags = window.lt.features;
			if (featureFlags) {
				const viewScheduleFeature = featureFlags[featureFlags.findIndex( (x: any) => x.featureId === 'bioschedulebutton')];
				const viewServicesFeature = featureFlags[featureFlags.findIndex( (x: any) => x.featureId === 'popviewservices')];
				if (viewScheduleFeature) {
					this.viewScheduleButton = viewScheduleFeature.enabled;
				}
				if (viewServicesFeature) {
					this.viewServicesButton = viewServicesFeature.enabled;
				}
				const biosSpringApifeature = featureFlags[featureFlags.findIndex( (x: any) => x.featureId === 'biosSpringApi')];
				if(biosSpringApifeature && biosSpringApifeature.enabled)
				this.useSpringApi = true;
			}
		});
	},
});
